import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import api from '../../services/api';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  School as SchoolIcon,
  Add as AddIcon,
  PlayArrow as PlayIcon
} from '@mui/icons-material';

function SeanceCreationScreen() {
  const navigate = useNavigate();
  const [creationMode, setCreationMode] = useState('prompt'); // 'prompt' or 'questionnaire'
  const [formData, setFormData] = useState({
    title: '',
    theme: '',
    age: '8-10',
    level: 'easy',
    language: 'French',
    number_of_sequences: 8,
    prompt: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  const ageOptions = [
    { value: '4-6', label: '4-6 ans' },
    { value: '6-8', label: '6-8 ans' },
    { value: '8-10', label: '8-10 ans' },
    { value: '10-12', label: '10-12 ans' },
    { value: '12-14', label: '12-14 ans' },
    { value: '14-16', label: '14-16 ans' }
  ];

  const levelOptions = [
    { value: 'easy', label: 'Débutant' },
    { value: 'intermediate', label: 'Intermédiaire' },
    { value: 'advanced', label: 'Avancé' }
  ];

  const subjectOptions = [
    'Shabbat',
    'Hanukkah',
    'Passover',
    'Rosh Hashanah',
    'Yom Kippur',
    'Sukkot',
    'Purim',
    'Talmud',
    'Torah',
    'Midrash',
    'Halacha',
    'Moussar'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form submission started:', { creationMode, formData });
    
    if (creationMode === 'questionnaire' && !formData.title.trim()) {
      setError('Le titre est obligatoire en mode questionnaire');
      return;
    }
    
    if (creationMode === 'prompt' && !formData.prompt.trim()) {
      setError('Le prompt est obligatoire en mode prompt');
      return;
    }

    if (creationMode === 'questionnaire' && !formData.theme.trim()) {
      setError('Le sujet est obligatoire en mode questionnaire');
      return;
    }

    // Validate number of sequences
    const numSequences = typeof formData.number_of_sequences === 'number' 
      ? formData.number_of_sequences 
      : parseInt(formData.number_of_sequences, 10);
    
    if (isNaN(numSequences) || numSequences < 1 || numSequences > 20) {
      setError('Le nombre de séquences doit être entre 1 et 20');
      return;
    }

    console.log('Validation passed, starting API call...');
    setIsCreating(true);
    setError(null);

    try {
      if (creationMode === 'prompt') {
        // Use the new API call for prompt-only mode
        const response = await api.seance.createSeanceFromPrompt(formData.prompt, formData, 'prompt_only');
        console.log('Seance created:', response);
        
        // Navigate to the new session using the response data
        // The API returns: { success: true, data: { seance_id: "...", seance: {...} } }
        const sessionId = response.data?.seance_id || response.data?.seance?.id || `session_${Date.now()}`;
        navigate(`/seance/${sessionId}`);
      } else {
        // Use the API call for questionnaire mode
        const response = await api.seance.createSeanceFromPrompt(formData.prompt || '', formData, 'mixed');
        console.log('Seance created:', response);
        
        // Navigate to the new session using the response data
        const sessionId = response.data?.seance_id || response.data?.seance?.id || `session_${Date.now()}`;
        navigate(`/seance/${sessionId}`);
      }
    } catch (err) {
      console.error('Error creating session:', err);
      setError(`Erreur lors de la création de la séance: ${err.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#F5F7FA' }}>
      <Navigation />
      <Container maxWidth="md" sx={{ py: 3 }}>
        <Card sx={{ 
          borderRadius: 4, 
          bgcolor: '#FFFFFF', 
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ 
              mb: 4,
              textAlign: 'center'
            }}>
              <Typography variant="h3" component="h1" sx={{ 
                fontWeight: 'bold', 
                color: '#E65100',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                justifyContent: 'center'
              }}>
                <SchoolIcon sx={{ fontSize: '2.5rem' }} />
                Créer une nouvelle séance
              </Typography>
              
              <Typography variant="h6" sx={{ color: '#7F8C8D' }}>
                Configurez votre séance éducative avec le nouveau système de blocs modulaires
              </Typography>
            </Box>

            {/* Creation Mode Toggle */}
            <Card sx={{ mb: 3, borderRadius: 3, bgcolor: '#F8F9FA' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
                  Mode de création
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant={creationMode === 'prompt' ? 'contained' : 'outlined'}
                    onClick={() => setCreationMode('prompt')}
                    startIcon={<span style={{ fontSize: '1.2rem' }}>✍️</span>}
                    sx={{
                      flex: 1,
                      py: 2,
                      borderRadius: '12px',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      ...(creationMode === 'prompt' ? {
                        background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)'
                        }
                      } : {
                        borderColor: '#3498DB',
                        color: '#3498DB',
                        '&:hover': {
                          borderColor: '#2980B9',
                          backgroundColor: '#E8F4FD'
                        }
                      })
                    }}
                  >
                    À partir d'un prompt
                  </Button>
                  
                  <Button
                    variant={creationMode === 'questionnaire' ? 'contained' : 'outlined'}
                    onClick={() => setCreationMode('questionnaire')}
                    startIcon={<span style={{ fontSize: '1.2rem' }}>📋</span>}
                    sx={{
                      flex: 1,
                      py: 2,
                      borderRadius: '12px',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      ...(creationMode === 'questionnaire' ? {
                        background: 'linear-gradient(135deg, #27AE60 0%, #2ECC71 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #229954 0%, #27AE60 100%)'
                        }
                      } : {
                        borderColor: '#27AE60',
                        color: '#27AE60',
                        '&:hover': {
                          borderColor: '#229954',
                          backgroundColor: '#E8F5E8'
                        }
                      })
                    }}
                  >
                    À partir d'un questionnaire
                  </Button>
                </Box>
                
                <Typography variant="body2" sx={{ mt: 2, color: '#6b7280', textAlign: 'center' }}>
                  {creationMode === 'prompt' 
                    ? '💡 Écrivez un prompt détaillé pour générer votre séance' 
                    : '📝 Répondez à quelques questions pour créer votre séance'
                  }
                </Typography>
              </CardContent>
            </Card>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {creationMode === 'prompt' ? (
                  <>
                    {/* Prompt Mode - Simple form with prompt */}
                    <Card sx={{ borderRadius: 3, bgcolor: '#F8F9FA' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h5" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
                          Informations de base
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            label="Prompt détaillé"
                            value={formData.prompt}
                            onChange={(e) => handleInputChange('prompt', e.target.value)}
                            placeholder="Ex: Créez une séance sur Shabbat pour des enfants de 8-10 ans. Focus sur l'allumage des bougies, l'aspect familial, et utilisez un langage simple avec des histoires personnelles..."
                            multiline
                            rows={6}
                            fullWidth
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px'
                              }
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <>
                    {/* Questionnaire Mode - Full form */}
                    {/* Basic Information */}
                    <Card sx={{ borderRadius: 3, bgcolor: '#F8F9FA' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h5" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
                          Informations de base
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <TextField
                            label="Titre de la séance"
                            value={formData.title}
                            onChange={(e) => handleInputChange('title', e.target.value)}
                            placeholder="Ex: Shabbat - The Candles"
                            fullWidth
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px'
                              }
                            }}
                          />
                          
                          <TextField
                            label="Sujet principal"
                            value={formData.theme}
                            onChange={(e) => handleInputChange('theme', e.target.value)}
                            placeholder="Ex: Shabbat, Hanukkah, Torah, etc."
                            fullWidth
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px'
                              }
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Target Audience */}
                    <Card sx={{ borderRadius: 3, bgcolor: '#F8F9FA' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h5" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
                          Public cible
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <FormControl fullWidth sx={{ borderRadius: '12px' }}>
                            <InputLabel>Âge</InputLabel>
                            <Select
                              value={formData.age}
                              onChange={(e) => handleInputChange('age', e.target.value)}
                              label="Âge"
                              sx={{ borderRadius: '12px' }}
                            >
                              {ageOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <FormControl fullWidth sx={{ borderRadius: '12px' }}>
                            <InputLabel>Niveau</InputLabel>
                            <Select
                              value={formData.level}
                              onChange={(e) => handleInputChange('level', e.target.value)}
                              label="Niveau"
                              sx={{ borderRadius: '12px' }}
                            >
                              {levelOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          
                          <FormControl fullWidth sx={{ borderRadius: '12px' }}>
                            <InputLabel>Langue</InputLabel>
                            <Select
                              value={formData.language}
                              onChange={(e) => handleInputChange('language', e.target.value)}
                              label="Langue"
                              sx={{ borderRadius: '12px' }}
                            >
                              <MenuItem value="French">Français</MenuItem>
                              <MenuItem value="English">English</MenuItem>
                              <MenuItem value="Hebrew">עברית</MenuItem>
                              <MenuItem value="Italian">Italien</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Structure */}
                    <Card sx={{ borderRadius: 3, bgcolor: '#F8F9FA' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h5" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
                          Structure de la séance
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                            label="Nombre de séquences"
                            type="number"
                            value={formData.number_of_sequences}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              if (inputValue === '') {
                                handleInputChange('number_of_sequences', '');
                                return;
                              }
                              const value = parseInt(inputValue, 10);
                              if (!isNaN(value)) {
                                if (value >= 1 && value <= 20) {
                                  handleInputChange('number_of_sequences', value);
                                } else if (value > 20) {
                                  handleInputChange('number_of_sequences', 20);
                                } else if (value < 1) {
                                  handleInputChange('number_of_sequences', 1);
                                }
                              }
                            }}
                            inputProps={{
                              min: 1,
                              max: 20,
                              step: 1
                            }}
                            helperText="Entre 1 et 20 séquences"
                            fullWidth
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px'
                              }
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Prompt Section */}
                    <Card sx={{ borderRadius: 3, bgcolor: '#F8F9FA' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h5" sx={{ mb: 2, color: '#2C3E50', fontWeight: 'bold' }}>
                          Instructions supplémentaires (optionnel)
                        </Typography>
                        
                        <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
                          Ajoutez des instructions spécifiques pour personnaliser le contenu de votre séance
                        </Typography>
                        
                        <TextField
                          label="Prompt ou instructions"
                          value={formData.prompt}
                          onChange={(e) => handleInputChange('prompt', e.target.value)}
                          placeholder="Ex: Focus sur l'aspect familial, inclure des histoires personnelles, utiliser un langage très simple..."
                          multiline
                          rows={4}
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '12px'
                            }
                          }}
                        />
                        
                        <Typography variant="caption" sx={{ mt: 1, color: '#9ca3af', display: 'block' }}>
                          💡 Ces instructions aideront l'IA à générer du contenu plus adapté à vos besoins spécifiques.
                        </Typography>
                      </CardContent>
                    </Card>
                  </>
                )}
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button
                  onClick={() => navigate('/seance')}
                  variant="outlined"
                  sx={{
                    borderColor: '#6b7280',
                    color: '#6b7280',
                    borderRadius: '20px',
                    px: 3,
                    py: 1,
                    fontWeight: 'bold',
                    '&:hover': {
                      borderColor: '#374151',
                      backgroundColor: '#F3F4F6'
                    }
                  }}
                >
                  Annuler
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isCreating}
                  startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
                  sx={{
                    background: 'linear-gradient(135deg, #3498DB 0%, #2980B9 100%)',
                    borderRadius: '20px',
                    px: 3,
                    py: 1,
                    fontWeight: 'bold',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2980B9 0%, #1F618D 100%)'
                    },
                    '&:disabled': {
                      background: '#BDBDBD'
                    }
                  }}
                >
                  {isCreating ? 'Création...' : 'Créer et générer'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
}

export default SeanceCreationScreen;